import useRequest from "@/lib/request/useRequest";
import { BaseField } from "@/types/field";
import {
    object,
    date,
    number,
    string,
    InferType,
} from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
    Button,
    ModalContent,
    ModalBody,
    ModalHeader,
    Modal,
    ModalFooter,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromGroundWater } from "@fortawesome/free-solid-svg-icons";
import DateInput from "@/components/inputs/DateInput";
import NumberInputComponent from "@/components/inputs/NumberInput";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import { useI18n } from "@/lib/i18n/useI18n";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const irrigationSchema = object().shape({
    start_date: date().required(),
    depth: number().required().min(0),
    note: string().nullable(),
});

type BulkLogIrrigationFormData = InferType<typeof irrigationSchema>;

function BulkLogIrrigation({
    fields,
    onFinish,
}: {
    fields: BaseField[];
    onFinish: () => void;
}) {
    const { t } = useTranslation("operations");
    const { formatUnit } = useI18n();
    const { useMutateRequest } = useRequest();
    const bulkLogIrrigationMutation = useMutateRequest({
        url: "irrigation_operations/bulk_upsert/",
        method: "put",
        successMessage: "Logged irrigation operations successfully.",
        errorMessage: "Failed to log irrigation operations.",
    });
    const form = useForm<BulkLogIrrigationFormData>({
        resolver: yupResolver(irrigationSchema),
        defaultValues: {
            start_date: dayjs().toDate(),
            depth: undefined,
            note: undefined,
        },
    });

    const saveIrrigationOperations = (data: BulkLogIrrigationFormData) => {
        const payload = fields.map((field) => ({
            field: field.id,
            irrigation_system: field.irrigation_system_configuration.default_system,
            start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
            irrigated_rates: [{ irrigated: true, rate: data.depth }],
            note: data.note,
        }));
        bulkLogIrrigationMutation.mutate({ data: payload }, {
            onSuccess: () => {
                onFinish();
            },
        });
    };


    return (
        <Modal isOpen size="md" onClose={onFinish}>
            <ModalContent>
                <ModalHeader>Bulk Log Irrigation</ModalHeader>
                <ModalBody>
                    <FormProvider {...form}>
                        <form>
                            <div className="flex flex-col gap-2">
                                <DateInput
                                    name="start_date"
                                    label={t("date")}
                                    variant="flat"
                                    isRequired
                                    className="w-full"
                                    size="sm"
                                    labelPlacement="outside"
                                    showMonthAndYearPickers
                                />
                                <NumberInputComponent
                                    name="depth"
                                    label={`${t("depth")} (${formatUnit("in", "cm")})`}
                                    variant="flat"
                                    size="sm"
                                    placeholder={t("depth")}
                                    className="w-full"
                                    min={0}
                                    step={0.1}
                                    isRequired
                                    labelPlacement="outside"
                                />
                                <TextAreaInput
                                    className="flex-1"
                                    name="note"
                                    label={t("note")}
                                    variant="flat"
                                    size="sm"
                                    placeholder={t("note")}
                                    labelPlacement="outside"
                                />
                            </div>
                        </form>
                    </FormProvider>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        fullWidth
                        onPress={() => {
                            form.trigger();
                            form.handleSubmit(saveIrrigationOperations)();
                        }}
                    >
                        <FontAwesomeIcon icon={faArrowUpFromGroundWater} className="mr-2" />
                        Log Irrigations
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default BulkLogIrrigation;